import {
  COOKIE_CONSENT_EVENT,
  CookieCategoryName,
  canWriteCookieCategory,
  isConsentReady,
} from './categories';
import { readCookie } from './readCookie';
import { writeCookie, WriteCookieOptions } from './writeCookie';

// How long the async helpers wait for the cookie-consent script to
// dispatch a consent event before falling through to a deny-by-default
// read. Non-rejecting on timeout keeps callers free of `try/catch`.
const DEFAULT_TIMEOUT_MS = 10_000;

/** Shared by every async consent helper. */
export type WaitForConsentOptions = {
  timeoutMs?: number;
};

/**
 * Options shape `writeCookieAsync` accepts: the standard
 * `WriteCookieOptions` plus an optional `timeoutMs`. Exported so
 * consumer wrappers can type their own helpers without re-deriving
 * the intersection.
 */
export type WriteCookieAsyncOptions = WriteCookieOptions &
  WaitForConsentOptions;

/**
 * Resolves as soon as consent state can be answered: either
 * `isConsentReady()` was already true, or the cookie-consent script
 * dispatched its consent event before `timeoutMs` elapsed. Resolves
 * (never rejects) on timeout and in non-browser environments, so
 * callers can fall through to a deny-by-default read without
 * `try / catch`.
 */
export const waitForConsentReady = async ({
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: WaitForConsentOptions = {}): Promise<void> => {
  if (typeof window === 'undefined') return;
  if (isConsentReady()) return;
  await new Promise<void>((resolve) => {
    // Whichever signal fires first (consent event or timeout), `settle`
    // removes the other so it can't re-fire. `removeEventListener` and
    // `clearTimeout` are both idempotent on already-fired sources, and
    // JavaScript's single-threaded execution model rules out re-entry,
    // so no `settled` flag is needed. `let` is unavoidable here because
    // `settle` and `timeoutHandle` reference each other circularly.
    // eslint-disable-next-line prefer-const
    let timeoutHandle: ReturnType<typeof setTimeout>;
    const settle = () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, settle);
      clearTimeout(timeoutHandle);
      resolve();
    };
    timeoutHandle = setTimeout(settle, timeoutMs);
    window.addEventListener(COOKIE_CONSENT_EVENT, settle);
  });
};

/**
 * Async equivalent of `canWriteCookieCategory`. Awaits consent
 * resolution so first-page-visit callers don't get a false "no consent"
 * while OneTrust is still loading.
 *
 * The returned boolean is a point-in-time snapshot: consent can change
 * after this resolves (the user revokes via the OneTrust banner, etc.).
 * Use `writeCookie` / `writeCookieAsync` to commit cookies — both re-check
 * consent at write time. Don't cache this boolean to gate a raw
 * `document.cookie = ...` write.
 */
export const canWriteCookieCategoryAsync = async (
  category: CookieCategoryName,
  options: WaitForConsentOptions = {}
): Promise<boolean> => {
  await waitForConsentReady(options);
  return canWriteCookieCategory(category);
};

/**
 * Async equivalent of `writeCookie`. Recommended for any non-essential
 * cookie that may be written during the consent-loading window
 * (e.g. a `visitor_id` cookie set on first-page visit).
 */
export const writeCookieAsync = async (
  name: string,
  value: string,
  options: WriteCookieAsyncOptions
): Promise<boolean> => {
  const { timeoutMs, ...writeOptions } = options;
  await waitForConsentReady({ timeoutMs });
  return writeCookie(name, value, writeOptions);
};

/**
 * Async equivalent of `readCookie` for values written elsewhere on the
 * page during the consent-loading window. Pairs naturally with
 * `writeCookieAsync` for read-after-write call shapes, and lets
 * consumers emit `page_view`-style events with the resolved cookie
 * value instead of `undefined`.
 */
export const readCookieAsync = async (
  name: string,
  options: WaitForConsentOptions = {}
): Promise<string | undefined> => {
  await waitForConsentReady(options);
  return readCookie(name);
};
